import React from 'react';
import useWebSocket from 'react-use-websocket';
import moment from 'moment-timezone';
import { useResilientQuery } from '@famly/stat_apollo-query';
import * as Queries from './queries';
const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    }
    catch (e) {
        return null;
    }
};
const isJwtExpired = (token) => {
    if (!token) {
        return false;
    }
    const decodedToken = parseJwt(token);
    return moment.unix(decodedToken.exp).isBefore(moment());
};
/**
 * Listen for biometric events to unlock signin app
 *
 * @param userDetectedCallBack Callback to call when a user is detected. The function will receive the pinCode of of the user
 * @returns An object containing whether the socket is open, if the client i subscribed to events and whether doorlocking is enabled at all.
 */
export function useListenForBiometricEvents(userDetectedCallBack) {
    const { data: unlockEventsJWT, refetch } = useResilientQuery(Queries.GetUnlockEventsJWT, { fetchPolicy: 'network-only' });
    // If the JWT is not available, we do not want the websocket to open. If the url is null, the useWebSocket hook will not create the connection
    const socketUrl = (unlockEventsJWT === null || unlockEventsJWT === void 0 ? void 0 : unlockEventsJWT.doorLocking.subscribeToUnlockEvents) ? 'wss://ws.famly.co/ws' : null;
    const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
        share: true, // We want to share the same connection across all the different subscribers
        retryOnError: true,
        shouldReconnect: () => {
            const tokenExpired = isJwtExpired(unlockEventsJWT === null || unlockEventsJWT === void 0 ? void 0 : unlockEventsJWT.doorLocking.subscribeToUnlockEvents);
            // refresh token if it is expired, restarting the connection by finishing the fetching
            if (tokenExpired) {
                refetch();
            }
            // keep open if not expired, close otherwise
            return !tokenExpired;
        },
        onOpen: () => {
            if (unlockEventsJWT === null || unlockEventsJWT === void 0 ? void 0 : unlockEventsJWT.doorLocking.subscribeToUnlockEvents) {
                sendMessage(unlockEventsJWT.doorLocking.subscribeToUnlockEvents);
            }
        },
        reconnectAttempts: 30,
        reconnectInterval: 3000,
    });
    React.useEffect(() => {
        if (lastMessage === null) {
            return;
        }
        const parsedData = JSON.parse(lastMessage.data);
        if (parsedData.type === 'UnlockSignInDevice' && parsedData.pin) {
            userDetectedCallBack(parsedData.pin);
        }
    }, [lastMessage, userDetectedCallBack]);
    return React.useMemo(() => {
        var _a;
        return ({
            connectionState: readyState,
            isSubscribed: (lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.data) && ((_a = JSON.parse(lastMessage.data)) === null || _a === void 0 ? void 0 : _a.type) === 'Unsubscribe',
            enabled: Boolean(unlockEventsJWT === null || unlockEventsJWT === void 0 ? void 0 : unlockEventsJWT.doorLocking.subscribeToUnlockEvents),
        });
    }, [readyState, lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.data, unlockEventsJWT === null || unlockEventsJWT === void 0 ? void 0 : unlockEventsJWT.doorLocking.subscribeToUnlockEvents]);
}
